/**
 * Standardized Enums
 * 
 * This file contains all enum definitions for the application, following the standardization guidelines:
 * 1. Enum keys use UPPERCASE for TypeScript clarity
 * 2. Enum values use lowercase to match Supabase database columns
 * 3. Each enum has a corresponding constant object with the same values
 * 4. Type guards are provided for runtime validation
 */

// ======= STATUS ENUMS =======

/**
 * Activity Status Enum
 * Represents the status of an activity in the system
 * Database column: activities.status
 */
export enum ActivityStatus {
  PLANNED = 'planned',
  IMPLEMENTED = 'implemented',
  CANCELLED = 'cancelled'
}

// Constants for ActivityStatus
export const ACTIVITY_STATUSES = {
  PLANNED: 'planned' as const,
  IMPLEMENTED: 'implemented' as const,
  CANCELLED: 'cancelled' as const,
};

// Type guard for ActivityStatus
export function isValidActivityStatus(value: unknown): value is ActivityStatus {
  return (
    typeof value === 'string' &&
    Object.values(ACTIVITY_STATUSES).includes(value as ActivityStatus)
  );
}

/**
 * GroupSession Status Enum
 * Represents the status of a group session
 * Database column: group_sessions.status
 */
export enum GroupSessionStatus {
  UPCOMING = 'upcoming',
  IMPLEMENTED = 'implemented',
  CANCELLED = 'cancelled'
}

// Constants for GroupSessionStatus
export const GROUP_SESSION_STATUSES = {
  UPCOMING: 'upcoming' as const,
  IMPLEMENTED: 'implemented' as const,
  CANCELLED: 'cancelled' as const,
};



// Type guard for GroupSessionStatus
export function isValidGroupSessionStatus(value: unknown): value is GroupSessionStatus {
  return (
    typeof value === 'string' &&
    Object.values(GROUP_SESSION_STATUSES).includes(value as GroupSessionStatus)
  );
}

/**
 * Artifact Processing Status Enum
 * Represents the processing status of an artifact
 * Database column: artifacts.processing_status
 */
export enum ArtifactProcessingStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

// Constants for ArtifactProcessingStatus
export const ARTIFACT_PROCESSING_STATUSES = {
  PENDING: 'pending' as const,
  PROCESSING: 'processing' as const,
  COMPLETED: 'completed' as const,
  FAILED: 'failed' as const,
};

// Type guard for ArtifactProcessingStatus
export function isValidArtifactProcessingStatus(value: unknown): value is ArtifactProcessingStatus {
  return (
    typeof value === 'string' &&
    Object.values(ARTIFACT_PROCESSING_STATUSES).includes(value as ArtifactProcessingStatus)
  );
}

/**
 * Transcription Status Enum
 * Represents the status of a transcription
 * Database column: transcriptions.status
 * Aligned with ArtifactProcessingStatus
 */
export enum TranscriptionStatus {
  QUEUED = 'queued',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

// Constants for TranscriptionStatus
export const TRANSCRIPTION_STATUSES = {
  QUEUED: 'queued' as const,
  PROCESSING: 'processing' as const,
  COMPLETED: 'completed' as const,
  FAILED: 'failed' as const,
};

// Type guard for TranscriptionStatus
export function isValidTranscriptionStatus(value: unknown): value is TranscriptionStatus {
  return (
    typeof value === 'string' &&
    Object.values(TRANSCRIPTION_STATUSES).includes(value as TranscriptionStatus)
  );
}

// ======= TYPE ENUMS =======

/**
 * Activity Type Enum
 * Represents the type of an activity
 * Database column: activities.type
 */
export enum ActivityType {
  DISCUSSION = 'discussion',
  PRESENTATION = 'presentation',
  WORKSHOP = 'workshop',
  BREAKOUT = 'breakout',
  EXERCISE = 'exercise',
  COLLECTIVE_WISDOM = 'collective_wisdom',
  OTHER = 'other'
}

// Constants for ActivityType
export const ACTIVITY_TYPES = {
  DISCUSSION: 'discussion' as const,
  PRESENTATION: 'presentation' as const,
  WORKSHOP: 'workshop' as const,
  BREAKOUT: 'breakout' as const,
  EXERCISE: 'exercise' as const,
  COLLECTIVE_WISDOM: 'collective_wisdom' as const,
  OTHER: 'other' as const,
};

// Type guard for ActivityType
export function isValidActivityType(value: unknown): value is ActivityType {
  return (
    typeof value === 'string' &&
    Object.values(ACTIVITY_TYPES).includes(value as ActivityType)
  );
}

/**
 * Artifact Type Enum
 * Represents the type of an artifact
 * Database column: artifacts.type
 */
export enum ArtifactType {
  AUDIO = 'audio',
  IMAGE = 'image',
  DOCUMENT = 'document',
  PDF = 'pdf',
  TEXT = 'text',
  VIDEO = 'video',
  OTHER = 'other'
}

// Constants for ArtifactType
export const ARTIFACT_TYPES = {
  AUDIO: 'audio' as const,
  IMAGE: 'image' as const,
  DOCUMENT: 'document' as const,
  PDF: 'pdf' as const,
  TEXT: 'text' as const,
  VIDEO: 'video' as const,
  OTHER: 'other' as const,
};



// Type guard for ArtifactType
export function isValidArtifactType(value: unknown): value is ArtifactType {
  return (
    typeof value === 'string' &&
    Object.values(ARTIFACT_TYPES).includes(value as ArtifactType)
  );
}

// ======= ROLE ENUMS =======

/**
 * User Role Enum
 * Represents the role of a user in the system
 * Database column: profiles.role (user_role enum)
 * 
 * IMPORTANT: These values must match the PostgreSQL enum 'user_role'
 * Current database values: 'admin', 'facilitator', 'user'
 */
export enum UserRole {
  ADMIN = 'admin',
  FACILITATOR = 'facilitator',
  USER = 'user'
}

/**
 * Group Session Role Enum
 * Represents the role of a user in a group session
 * Database columns: group_session_members.role, session_invitations.role
 * 
 * IMPORTANT: These values must match the PostgreSQL enum values
 * Current database values: 'owner', 'editor', 'viewer', 'facilitator', 'documentor', 'participant'
 * 
 * Role descriptions:
 * - owner → Full control over the session (typically the creator)
 * - facilitator → Co-facilitator with edit rights but cannot delete
 * - documentor → Focus on documentation, artifacts, and transcriptions
 * - editor → Legacy role, being phased out in favor of facilitator/documentor
 * - participant → Limited access, can submit feedback and personal artifacts
 * - viewer → Legacy role, being phased out in favor of participant
 */
export enum GroupSessionRole {
  OWNER = 'owner',
  FACILITATOR = 'facilitator',
  DOCUMENTOR = 'documentor',
  PARTICIPANT = 'participant',
  // Legacy roles - kept for backward compatibility
  EDITOR = 'editor',
  VIEWER = 'viewer'
}

// Constants for GroupSessionRole
export const GROUP_SESSION_ROLES = {
  OWNER: 'owner' as const,
  FACILITATOR: 'facilitator' as const,
  DOCUMENTOR: 'documentor' as const,
  PARTICIPANT: 'participant' as const,
  // Legacy roles
  EDITOR: 'editor' as const,
  VIEWER: 'viewer' as const
};

// Type guard for GroupSessionRole
export function isValidGroupSessionRole(value: unknown): value is GroupSessionRole {
  if (typeof value !== 'string') return false;
  
  // Case-insensitive comparison for better compatibility
  const normalizedValue = value.toLowerCase();
  
  return Object.values(GROUP_SESSION_ROLES).includes(normalizedValue as GroupSessionRole);
}

/**
 * User registration status for invitation system
 */
export enum UserStatus {
  UNKNOWN = 'unknown',
  REGISTERED = 'registered', // Existing app user
  NEW = 'new'               // New user who needs an account
}

// Constants for UserRole
export const USER_ROLES = {
  ADMIN: 'admin' as const,
  FACILITATOR: 'facilitator' as const,
  USER: 'user' as const
};

// Type guard for UserRole
export function isValidUserRole(value: unknown): value is UserRole {
  if (typeof value !== 'string') return false;
  
  // Case-insensitive comparison for better compatibility
  const normalizedValue = value.toLowerCase();

  return normalizedValue === USER_ROLES.ADMIN ||
         normalizedValue === USER_ROLES.FACILITATOR ||
         normalizedValue === USER_ROLES.USER;
}

// Usage examples:
// 
// 1. For type annotations:
//    const status: ActivityStatus = ActivityStatus.COMPLETED;
// 
// 2. For value comparisons:
//    if (status === ActivityStatus.COMPLETED) { ... }
// 
// 3. For UI/mapping:
//    const statusLabel = statusLabels[ACTIVITY_STATUSES.COMPLETED];
